/*
 * share — Level 2 (CSP build) three-tier progressive enhancement over a plain
 * permalink anchor.
 *
 * The anchor is a real link to the canonical URL, so with no JS it simply
 * navigates. When JS is present the click is intercepted and the best available
 * mechanism is chosen by FEATURE DETECTION (never browser sniffing):
 *
 *   1. navigator.share  — native OS share sheet (mobile / supporting browsers).
 *   2. navigator.clipboard.writeText — copy the URL, confirm with a toast.
 *   3. neither available — do NOT preventDefault; the anchor navigates as normal.
 *
 * The server owns the canonical URL + share metadata; they arrive on the element
 * as data-share-* attributes (and the href), never as JS-passed literals.
 *
 * Registered in resources/js/app.js as: Alpine.data('share', share)
 * Paired with resources/views/components/interactive/share.antlers.html.
 */
export default function share() {
    return {
        init() {},

        /**
         * @param {Event} event  Passed automatically by Alpine's `@click="share"`.
         */
        async share(event) {
            const el = this.$root;
            const url = el.dataset.shareUrl || el.href;
            const title = el.dataset.shareTitle || document.title;
            const text = el.dataset.shareText || '';

            // Tier 1 — native share sheet.
            if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
                event.preventDefault();
                try {
                    await navigator.share({ title, text, url });
                } catch (err) {
                    // AbortError = the user dismissed the sheet. Nothing to recover.
                }
                return;
            }

            // Tier 2 — copy to clipboard + confirm with a toast.
            if (
                typeof navigator !== 'undefined' &&
                navigator.clipboard &&
                typeof navigator.clipboard.writeText === 'function'
            ) {
                event.preventDefault();
                try {
                    await navigator.clipboard.writeText(url);
                    this.$store.toasts.success('Link copied to clipboard');
                } catch (err) {
                    // Clipboard denied — recover by navigating to the canonical URL.
                    window.location.assign(url);
                }
                return;
            }

            // Tier 3 — no preventDefault: the anchor navigates as an ordinary link.
        },

        destroy() {},
    };
}
